import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { auth } from '../../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import './HomeView.css';

interface LastWorkout {
  weight: number;
  date: Date;
}

const HomeView: React.FC = () => {
  const [user] = useAuthState(auth);
  const [lastWorkouts, setLastWorkouts] = useState<{ [key: string]: LastWorkout }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const workoutTypes = ["Bench Press", "Squat", "Deadlift", "Clean"];

  useEffect(() => {
    loadLastWorkouts();
  }, [user]);

  const loadLastWorkouts = async () => {
    if (!user?.uid) {
      setErrorMessage("No user logged in");
      setIsLoading(false);
      return;
    }

    try {
      const { db } = await import('../../firebase');
      const workoutsRef = collection(db, 'workouts');
      const q = query(
        workoutsRef,
        where('userId', '==', user.uid),
        orderBy('dateCompleted', 'desc'),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      const workouts: { [key: string]: LastWorkout } = {};

      snapshot.docs.forEach((doc) => {
        const data = doc.data();
        const type = data.type as string;
        const weight = data.weight as number;
        const date = data.dateCompleted?.toDate() as Date;

        if (type && weight && date && !workouts[type]) {
          workouts[type] = { weight, date };
        }
      });

      setLastWorkouts(workouts);
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error loading last workouts:', error);
      setErrorMessage(`Failed to load workouts: ${error.message}`);
      setIsLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="home-view">
      <div className="home-header">
        <div className="logo-container">
          <div className="strength-icon">💪</div>
        </div>
        <h1 className="app-title">Lions Strength Program</h1>
      </div>

      <div className="last-workouts-section">
        <h2>Last Workouts</h2>
        
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <div className="workout-list">
            {workoutTypes.map((type) => (
              <div key={type} className="workout-row">
                <div className="workout-info">
                  <span className="workout-type">{type}</span>
                </div>
                <div className="workout-stats">
                  {lastWorkouts[type] ? (
                    <>
                      <span className="workout-weight">
                        {Math.round(lastWorkouts[type].weight)} lbs
                      </span>
                      <span className="workout-date">
                        {formatDate(lastWorkouts[type].date)}
                      </span>
                    </>
                  ) : (
                    <span className="workout-not-started">Not Started</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomeView;
